import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { getMenuItemsForUser } from "../../utils/roleAccess";
import "../../styles/sidebar.css";

const Sidebar = ({ isOpen, onClose }) => {
  const { user } = useSelector((state) => state.grievances);
  const [collapsed, setCollapsed] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  // console.log(user ,'user')
  // Check window size and set mobile/collapsed state
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setCollapsed(!mobile && window.innerWidth <= 1200);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle clicks outside sidebar in mobile view
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    const closeOnOutsideClick = (e) => {
      if (
        !e.target.closest(".modern-sidebar") &&
        !e.target.closest(".sidebar-toggle-btn")
      ) {
        onClose?.();
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [isOpen, onClose, isMobile]);

  const menuItems = getMenuItemsForUser(user);

  // Toggle sidebar collapse
  const toggleSidebar = () => {
    if (isMobile) {
      onClose?.();
    } else {
      setCollapsed(!collapsed);
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && isMobile && (
        <div className="sidebar-overlay" onClick={onClose}></div>
      )}

      {/* Mobile toggle button */}
      {isMobile && !isOpen && (
        <button
          className="mobile-sidebar-toggle"
          onClick={onClose}
          aria-label="Open sidebar"
        >
          <i className="bi bi-chevron-right"></i>
        </button>
      )}

      {/* Sidebar */}
      <div
        className={`modern-sidebar ${isOpen ? "show" : ""} ${collapsed && !isMobile ? "sidebar-collapsed" : ""
          }`}
        role="navigation"
        aria-label="Main Navigation"
      >
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="brand-icon">
              <i className="bi bi-grid-1x2-fill" aria-hidden="true"></i>
              {(!collapsed || isMobile) && (
                <span className="brand-text">SAMPARK SETU</span>
              )}
            </div>
          </div>
          <button
            className="sidebar-toggle-btn"
            onClick={toggleSidebar}
            aria-label={
              isMobile
                ? "Close sidebar"
                : collapsed
                  ? "Expand sidebar"
                  : "Collapse sidebar"
            }
          >
            <i
              className={`bi ${isMobile
                ? "bi-x-lg"
                : collapsed
                  ? "bi-chevron-right"
                  : "bi-chevron-left"
                }`}
              aria-hidden="true"
            ></i>
          </button>
        </div>

        {/* Menu */}
        <div className="sidebar-menu-container">
          <ul className="sidebar-menu">
            {menuItems.map((item, index) => (
              <li
                key={index}
                className="sidebar-menu-item"
                onMouseEnter={() => setHoveredItem(index)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `sidebar-nav-link ${isActive ? "active" : ""} ${hoveredItem === index ? "hovered" : ""
                    }`
                  }
                  end={item.exact}
                  aria-label={item.label}
                  onClick={() => isMobile && onClose?.()}
                >
                  <div className="nav-icon">
                    <i className={`bi ${item.icon}`} aria-hidden="true"></i>
                  </div>
                  {(!collapsed || isMobile || hoveredItem === index) && (
                    <span
                      className={`nav-text ${hoveredItem === index && collapsed && !isMobile
                        ? "tooltip-text"
                        : ""
                        }`}
                    >
                      {item.label}
                    </span>
                  )}
                  {item.badge && (!collapsed || isMobile) && (
                    <span
                      className="nav-badge"
                      aria-label={`${item.badge} notifications`}
                    >
                      {item.badge}
                    </span>
                  )}
                </NavLink>
                {collapsed && !isMobile && item.badge && (
                  <span
                    className="collapsed-badge"
                    aria-label={`${item.badge} notifications`}
                  >
                    {item.badge}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="sidebar-footer">
          {(!collapsed || isMobile) && (
            <div className="sidebar-footer-text">
              © {new Date().getFullYear()} SourceDOTcom
            </div>
          )}
          <div
            className="sidebar-footer-icon"
            aria-label="Help"
            role="button"
            tabIndex={0}
          >
            <i className="bi bi-question-circle-fill" aria-hidden="true"></i>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(Sidebar);
